import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { persistStore } from "src/persist-store";
import type { PortfolioEntity, Transaction, ActivityEntry, ColorPreset } from "src/types/portfolio.types";
import { getIconForType } from "@/constants/portfolio.constants";
import { usePortfolioSeedData } from "@/hooks/portfolio/usePortfolioSeedData";
import { useIsDemo } from "@/hooks/common/useIsDemo";
import { useSelectedOrgId } from "@/hooks/common/useSelectedOrgId";

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export interface CreatePortfolioEntityData {
  name: string;
  type: string;
  colorPreset: ColorPreset;
  image?: string;
}

interface PortfolioContextType {
  entities: PortfolioEntity[];
  addEntity: (data: CreatePortfolioEntityData) => PortfolioEntity;
  activeEntityId: string | null;
  setActiveEntityId: (id: string | null) => void;
  activeEntity: PortfolioEntity | null;
  dealMap: Record<string, string[]>;
  transactions: Record<string, Transaction[]>;
  activity: Record<string, ActivityEntry[]>;
  isCreateDialogOpen: boolean;
  openCreateDialog: () => void;
  closeCreateDialog: () => void;
}

// --------------------------------------------------------------------------
// Persistence helpers
// --------------------------------------------------------------------------

const STORAGE_KEY = "portfolio-context";

interface PersistedEntity {
  id: string;
  name: string;
  type: string;
  image?: string;
  activeDeals: number;
  totalValue: string;
  color: string;
  bgClass: string;
  activeBg: string;
}

interface PersistedState {
  entities: PersistedEntity[];
  activeEntityId: string | null;
  dealMap: Record<string, string[]>;
}

function serializeEntities(entities: PortfolioEntity[]): PersistedEntity[] {
  return entities.map((e) => ({
    id: e.id,
    name: e.name,
    type: e.type,
    image: e.image,
    activeDeals: e.activeDeals,
    totalValue: e.totalValue,
    color: e.color,
    bgClass: e.bgClass,
    activeBg: e.activeBg,
  }));
}

function rehydrateEntities(raw: PersistedEntity[]): PortfolioEntity[] {
  return raw.map((e) => ({
    ...e,
    icon: getIconForType(e.type),
  }));
}

// --------------------------------------------------------------------------
// Context + Provider
// --------------------------------------------------------------------------

const PortfolioContext = createContext<PortfolioContextType | undefined>(undefined);

export const PortfolioProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const isDemo = useIsDemo();
  const selectedOrgId = useSelectedOrgId();
  const { seedEntities, seedDealMap, seedTransactions, seedActivity } = usePortfolioSeedData();
  const [entities, setEntities] = useState<PortfolioEntity[]>(seedEntities);
  const [activeEntityId, setActiveEntityId] = useState<string | null>(null);
  const [dealMap, setDealMap] = useState<Record<string, string[]>>(seedDealMap);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Transactions and activity are read-only — derive directly from seed data
  const transactions = seedTransactions;
  const activity = seedActivity;

  // Reset state when switching between demo and prod routes
  useEffect(() => {
    setEntities(seedEntities);
    setDealMap(seedDealMap);
    setActiveEntityId(null);
  }, [seedEntities, seedDealMap]);

  // Load persisted state — scoped by demo/prod
  const storageKey = isDemo
    ? `${STORAGE_KEY}-demo-${selectedOrgId || 'default'}`
    : STORAGE_KEY;
  useEffect(() => {
    const load = async () => {
      try {
        const stored = await persistStore.getSession<PersistedState>(storageKey);
        if (stored) {
          setEntities(prev => {
            const rehydrated = rehydrateEntities(stored.entities);
            return rehydrated.length > 0 ? rehydrated : prev;
          });
          setActiveEntityId(stored.activeEntityId);
          setDealMap(prev => ({ ...prev, ...stored.dealMap }));
        }
      } catch (err) {
        console.error("Error loading portfolio state:", err);
      }
    };
    load();
  }, [storageKey]);

  // Persist on change — scoped by demo/prod
  useEffect(() => {
    const persist = async () => {
      try {
        await persistStore.putSession<PersistedState>(storageKey, {
          entities: serializeEntities(entities),
          activeEntityId,
          dealMap,
        });
      } catch (err) {
        console.error("Error saving portfolio state:", err);
      }
    };
    persist();
  }, [entities, activeEntityId, dealMap, storageKey]);

  const addEntity = useCallback(
    (data: CreatePortfolioEntityData): PortfolioEntity => {
      const id = `pe-${Date.now()}`;
      const newEntity: PortfolioEntity = {
        id,
        name: data.name,
        type: data.type,
        icon: getIconForType(data.type),
        image: data.image,
        activeDeals: 0,
        totalValue: "$0",
        color: data.colorPreset.color,
        bgClass: data.colorPreset.bgClass,
        activeBg: data.colorPreset.activeBg,
      };
      setEntities((prev) => [...prev, newEntity]);
      setDealMap((prev) => ({ ...prev, [id]: [] }));
      return newEntity;
    },
    [],
  );

  const activeEntity = entities.find((e) => e.id === activeEntityId) ?? null;

  const openCreateDialog = useCallback(() => setIsCreateDialogOpen(true), []);
  const closeCreateDialog = useCallback(() => setIsCreateDialogOpen(false), []);

  const value: PortfolioContextType = {
    entities,
    addEntity,
    activeEntityId,
    setActiveEntityId,
    activeEntity,
    dealMap,
    transactions,
    activity,
    isCreateDialogOpen,
    openCreateDialog,
    closeCreateDialog,
  };

  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  );
};

export function usePortfolio(): PortfolioContextType {
  const ctx = useContext(PortfolioContext);
  if (!ctx) {
    throw new Error("usePortfolio must be used within a PortfolioProvider");
  }
  return ctx;
}

export function usePortfolioSafe(): PortfolioContextType | null {
  return useContext(PortfolioContext) ?? null;
}
